import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getAdminCourseDropoutApi, CourseDropout } from "../../features/analytics/api/adminAnalytics.api"
import { ChevronLeftIcon, EyeIcon, ExclamationTriangleIcon } from "@heroicons/react/16/solid"

const dropoutColor = (pct: number) => {
  if (pct >= 75) return { bar: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50" }
  if (pct >= 50) return { bar: "bg-amber-400",   text: "text-amber-700",   bg: "bg-amber-50" }
  return               { bar: "bg-red-400",      text: "text-red-700",     bg: "bg-red-50" }
}

export const CourseDropoutPage = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const [data, setData] = useState<CourseDropout | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    if (!courseId) return
    setLoading(true)
    setError(false)
    getAdminCourseDropoutApi(courseId)
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [courseId]) // eslint-disable-line

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Link to={`/admin/analytics/courses/${courseId}`}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors">
          <ChevronLeftIcon className="w-4 h-4" />
          Детали курса
        </Link>
        <span className="text-gray-300">/</span>
        <h2 className="text-base font-semibold text-gray-800">Анализ отсева</h2>
      </div>

      {error ? (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400 mb-2">Не удалось загрузить анализ отсева</p>
          <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
            Повторить
          </button>
        </div>
      ) : loading || !data ? (
        <div className="space-y-4">
          {[1, 2].map((i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl h-32 animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Legend */}
          <div className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl px-4 py-3">
            <span className="text-xs text-gray-400 font-medium">Прогресс урока:</span>
            {[
              { color: "bg-emerald-500", label: "> 75% — хорошо" },
              { color: "bg-amber-400",   label: "50–75% — средне" },
              { color: "bg-red-400",     label: "< 50% — проблема" },
            ].map((item) => (
              <span key={item.label} className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                {item.label}
              </span>
            ))}
          </div>

          {/* Modules */}
          {data.modules.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center text-gray-300 text-sm">
              Нет данных по этому курсу
            </div>
          ) : (
            <div className="space-y-4">
              {data.modules.map((mod) => (
                <div key={mod.module_id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                  {/* Module header */}
                  <div className="px-5 py-3.5 bg-gray-50/60 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-800 text-sm">{mod.title}</h3>
                  </div>

                  {/* Lessons */}
                  <div className="divide-y divide-gray-50">
                    {mod.lessons.map((lesson) => {
                      const pct = lesson.avg_dropout_percent
                      const clr = dropoutColor(pct)
                      return (
                        <div key={lesson.lesson_id} className="px-5 py-4 flex items-center gap-4">
                          {/* Title */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{lesson.title}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <div className="flex-1 max-w-[240px] h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${clr.bar}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${clr.text} ${clr.bg}`}>
                                {pct}%
                              </span>
                            </div>
                          </div>

                          {/* Completion */}
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-gray-400">Завершили</p>
                            <p className="text-sm font-semibold text-gray-700">{lesson.completion_rate}%</p>
                          </div>

                          {/* Viewers */}
                          <div className="text-right flex-shrink-0 flex items-center gap-1.5 text-xs text-gray-400">
                            <EyeIcon className="w-3.5 h-3.5" />
                            <span>{lesson.total_viewers.toLocaleString("ru")}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Note */}
          <p className="text-xs text-gray-400 text-center pb-2">
            avg_dropout_percent — средний % урока, на котором пользователи выходят
          </p>
        </>
      )}
    </div>
  )
}
